import React from 'react';
import { motion } from 'framer-motion';
import { Check, Salad, Heart, Dumbbell, Flame, Clock } from 'lucide-react';
import { KITS } from '../data/kits';
interface KitsSectionProps {
  selectedKitIds: string[];
  onToggleKit: (id: string) => void;
}
const ICONS = {
  Salad,
  Heart,
  Dumbbell
};
const BASE_INCLUSIONS = ['+100 recetas saludables', 'Planificación semanal y mensual', 'Lista de compras para supermercado y dietética', 'Acceso inmediato y de por vida'];
export function KitsSection({
  selectedKitIds,
  onToggleKit
}: KitsSectionProps) {
  return <section id="kits" className="py-24 bg-cream scroll-mt-20" data-id="element-392">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-393">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-394">
          {/* Urgency banner above headline */}
          <motion.div animate={{
          scale: [1, 1.03, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-2 rounded-full font-bold text-xs sm:text-sm uppercase tracking-wider mb-6 shadow-lg border border-red-400/40" data-id="element-395">
            <Flame className="w-4 h-4 text-yellow-300" data-id="element-396" />
            🔥 OFERTA SOLO POR HOY · 50% OFF · No se repite
            <Flame className="w-4 h-4 text-yellow-300" data-id="element-397" />
          </motion.div>
          <h2 className="text-3xl md:text-4xl lg:text-[40px] font-serif font-bold text-ink mb-6" data-id="element-398">
            Elegí el kit ideal para tu objetivo
          </h2>
          <p className="text-lg text-ink-light max-w-2xl mx-auto" data-id="element-399">
            Todos incluyen la base completa.
            <br data-id="element-400" />
            La diferencia está en los bonus específicos según tu necesidad.
          </p>
        </motion.div>
        <div className="grid lg:grid-cols-3 gap-8 items-start" data-id="element-401">
          {KITS.map((kit, index) => {
          const isSelected = selectedKitIds.includes(kit.id);
          const isPopular = kit.id === 'completa';
          const Icon = ICONS[kit.icon as keyof typeof ICONS];
          const kitImage = kit.image;
          return <motion.div key={kit.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} onClick={() => onToggleKit(kit.id)} className={`
                  relative bg-white rounded-[2rem] overflow-hidden cursor-pointer transition-all duration-300
                  ${isPopular ? 'lg:-mt-6 lg:mb-6 shadow-premium-hover ring-2 ring-combo-green' : 'shadow-premium border border-ink/5'}
                  ${isSelected ? 'ring-4 ring-gold ring-offset-4 ring-offset-cream' : 'hover:-translate-y-2 hover:shadow-premium-hover'}
                `} data-id="element-402">
                {/* Floating "SOLO POR HOY" corner ribbon */}
                <div className="absolute top-4 right-4 z-30" data-id="element-403">
                  <motion.div animate={{
                rotate: [-3, 3, -3]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="bg-yellow-400 text-red-700 px-3 py-1.5 rounded-lg font-extrabold text-[10px] sm:text-xs uppercase tracking-wider shadow-lg border-2 border-red-700" data-id="element-404">
                    ⏰ Solo hoy
                  </motion.div>
                </div>
                {/* Header */}
                <div className={`${kit.color} p-8 pt-10 text-white text-center relative overflow-hidden`} data-id="element-405">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-406" />
                  <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-bold tracking-wider mb-6" data-id="element-407">
                    {kit.badge}
                  </div>
                  <Icon className="w-12 h-12 mx-auto mb-4 opacity-90" data-id="element-408" />
                  <h3 className="font-serif text-2xl font-bold mb-2" data-id="element-409">
                    {kit.name}
                  </h3>
                </div>
                {/* Kit image (only for completa) */}
                {kitImage && <div className="bg-cream-dark px-4 pt-4 pb-2" data-id="element-410">
                    <motion.img src={kitImage} alt={`Mockup del ${kit.name}`} initial={{
                opacity: 0,
                scale: 0.95
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="w-full h-auto rounded-xl shadow-md" data-id="element-411" />
                  </div>}
                {/* Content */}
                <div className="p-8" data-id="element-412">
                  <p className="text-ink-light text-sm mb-6 text-center min-h-[40px]" data-id="element-413">
                    {kit.description}
                  </p>
                  {/* Pricing */}
                  <div className="text-center mb-6 bg-red-50 rounded-2xl p-5 border border-red-100" data-id="element-414">
                    <div className="flex items-center justify-center gap-2 text-xs font-bold text-red-700 uppercase tracking-wider mb-2" data-id="element-415">
                      <Clock className="w-3.5 h-3.5" data-id="element-416" />
                      Precio especial solo por hoy
                    </div>
                    <div className="text-ink-light line-through decoration-red-500/70 decoration-2 mb-1" data-id="element-417">
                      ${kit.oldPrice.toLocaleString('es-AR')}
                    </div>
                    <div className="flex items-center justify-center gap-3 mb-2" data-id="element-418">
                      <span className="text-4xl font-extrabold text-ink" data-id="element-419">
                        ${kit.price.toLocaleString('es-AR')}
                      </span>
                      <motion.span animate={{
                    scale: [1, 1.08, 1]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity
                  }} className="text-xs font-extrabold text-white bg-red-600 px-2.5 py-1.5 rounded-md shadow-md" data-id="element-420">
                        50% OFF
                      </motion.span>
                    </div>
                    <div className="text-sm font-bold text-combo-green" data-id="element-421">
                      ✓ Ahorrás $
                      {(kit.oldPrice - kit.price).toLocaleString('es-AR')} hoy
                    </div>
                  </div>
                  {/* Base inclusions */}
                  <div className="space-y-3 mb-6 pb-6 border-b border-ink/10" data-id="element-422">
                    <div className="font-bold text-xs text-ink uppercase tracking-wider mb-3" data-id="element-423">
                      Incluido en todos los kits:
                    </div>
                    {BASE_INCLUSIONS.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-424">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gold/15 flex items-center justify-center mt-0.5" data-id="element-425">
                          <Check className="w-3 h-3 text-gold" strokeWidth={3} data-id="element-426" />
                        </div>
                        <span className="text-sm text-ink font-medium" data-id="element-427">
                          {item}
                        </span>
                      </div>)}
                  </div>
                  {/* Bonuses */}
                  <div className="space-y-3 mb-8" data-id="element-428">
                    <div className="font-bold text-xs text-ink uppercase tracking-wider mb-3" data-id="element-429">
                      🎁 Bonus exclusivos de este kit:
                    </div>
                    {kit.bonuses.map((bonus, i) => <div key={i} className="flex items-start gap-3" data-id="element-430">
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full ${kit.color} flex items-center justify-center mt-0.5`} data-id="element-431">
                          <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-432" />
                        </div>
                        <span className="text-sm text-ink-light font-medium" data-id="element-433">
                          {bonus}
                        </span>
                      </div>)}
                  </div>
                  {/* Button */}
                  <motion.button animate={!isSelected ? {
                boxShadow: ['0 0 0 0 rgba(27,67,50,0.4)', '0 0 0 12px rgba(27,67,50,0)', '0 0 0 0 rgba(27,67,50,0)']
              } : {}} transition={{
                duration: 2,
                repeat: Infinity
              }} className={`
                      w-full py-4 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2
                      ${isSelected ? 'bg-ink text-white shadow-lg' : `${kit.color} text-white hover:opacity-95 shadow-md`}
                    `} data-id="element-434">
                    {isSelected ? <>
                        <Check className="w-5 h-5" data-id="element-435" /> KIT SELECCIONADO
                      </> : <>
                        QUIERO ESTE KIT <span aria-hidden="true" data-id="element-436">&rarr;</span>
                      </>}
                  </motion.button>
                  {/* Reassurance */}
                  <div className="mt-6 flex flex-col gap-2 text-xs text-ink-light/80 justify-center items-center" data-id="element-437">
                    <div className="flex items-center gap-1.5" data-id="element-438">
                      <Check className="w-3 h-3" data-id="element-439" /> Acceso inmediato
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-440">
                      <Check className="w-3 h-3" data-id="element-441" /> Pago seguro
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-442">
                      <Check className="w-3 h-3" data-id="element-443" /> Descarga instantánea
                    </div>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}